import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Globe, Lock } from 'lucide-react';
import { useThemeContext } from '@utils/hooks/use-theme-context';

type AccessSelectorProps = {
  isPrivate: boolean;
  onSelectShared: () => void;
  onSelectPrivate: () => void;
  disabled?: boolean;
};

/**
 * Two-card access chooser used by the create / edit folder modals.
 * Replaces the old pill toggle with a self-describing, modern segmented card.
 */
export const AccessSelector = ({
  isPrivate,
  onSelectShared,
  onSelectPrivate,
  disabled = false
}: AccessSelectorProps) => {
  const { t } = useTranslation();
  const { getColour, getBrandTint, getContrastTextColor } = useThemeContext();
  const [hovered, setHovered] = useState<'shared' | 'private' | null>(null);

  const accent = getColour('activeText');

  const options = [
    {
      key: 'shared' as const,
      selected: !isPrivate,
      icon: <Globe size={18} strokeWidth={2.1} />,
      title: t('shared'),
      description: t('shared-access-description'),
      onSelect: onSelectShared
    },
    {
      key: 'private' as const,
      selected: isPrivate,
      icon: <Lock size={18} strokeWidth={2.1} />,
      title: t('private'),
      description: t('private-access-description'),
      onSelect: onSelectPrivate
    }
  ];

  return (
    <div className="grid grid-cols-2 gap-3">
      {options.map(opt => {
        const isHovered = hovered === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={opt.onSelect}
            onMouseEnter={() => setHovered(opt.key)}
            onMouseLeave={() => setHovered(null)}
            disabled={disabled}
            aria-pressed={opt.selected}
            className="group relative flex flex-col gap-3 overflow-hidden rounded-2xl border p-4 text-left transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              borderColor: opt.selected
                ? accent
                : isHovered
                  ? accent
                  : getColour('hairline'),
              backgroundColor: opt.selected
                ? getBrandTint(0.07)
                : getColour('secondLayer'),
              boxShadow: opt.selected
                ? `0 0 0 1px ${accent}, 0 10px 24px -16px ${accent}`
                : 'none'
            }}
          >
            {/* selected check badge */}
            <span
              className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full transition-all duration-200"
              style={{
                backgroundColor: opt.selected ? accent : 'transparent',
                color: opt.selected
                  ? getContrastTextColor(accent)
                  : 'transparent',
                opacity: opt.selected ? 1 : 0,
                transform: opt.selected ? 'scale(1)' : 'scale(0.6)'
              }}
            >
              <Check size={12} strokeWidth={3} />
            </span>

            {/* icon chip */}
            <span
              className="flex h-10 w-10 items-center justify-center rounded-xl transition-colors duration-200"
              style={{
                backgroundColor: opt.selected
                  ? accent
                  : getColour('thirdLayer'),
                color: opt.selected
                  ? getContrastTextColor(accent)
                  : getColour('inactiveText')
              }}
            >
              {opt.icon}
            </span>

            <span className="flex flex-col gap-1 pr-4">
              <span
                className="text-sm font-semibold"
                style={{
                  color: opt.selected ? accent : getColour('normalText')
                }}
              >
                {opt.title}
              </span>
              <span
                className="text-xs leading-snug"
                style={{ color: getColour('inactiveText') }}
              >
                {opt.description}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
};
